import { Paper as MantinePaper } from "@mantine/core";
import styles from "./paper.module.scss";
import {
    type IProps,
    type Factory,
    type IPaperHeaderProps,
    type PaperHeaderFactory,
    type IPaperHeaderTitleProps,
    type IPaperContentProps,
    type PaperContentFactory,
    type PaperContentHeaderFactory,
    type IPaperContentHeaderProps,
    type PaperFooterFactory,
    type IPaperFooterProps,
    type IPaperContext,
} from "./paper.types";
import { clsx } from "clsx";
import { componentWrapper, polymorphicComponentWrapper } from "../../utilities";
import { Group } from "../group";
import { Title } from "../title";
import { Stack } from "../stack";
import { Text } from "../text";
import { Divider } from "../divider";
import { createContext, useContext, useMemo } from "react";

const PaperContext = createContext<IPaperContext>({});

/**
 * Paper component for displaying content in a card-like container.
 * @param {React.ReactNode} props.children - Content to display in the paper.
 * @param {string} props.rootStackGap - Gap spacing for the root stack layout.
 * @param {object} props.stackProps - Additional props for the internal Stack component.
 * @returns {JSX.Element} The Paper component.
 */
const Paper = polymorphicComponentWrapper<Factory, IProps>("Paper", ({ children, rootStackGap, ...props }, ref) => {
    const { stackProps, variant, ...otherProps } = props;
    const isSidebar = useMemo(() => variant === "sidebar", [variant]);

    return (
        <PaperContext.Provider value={{ variant }}>
            <MantinePaper
                component="div"
                ref={ref}
                classNames={{ root: styles["container"] }}
                p={isSidebar ? 0 : (props.p ?? "lg")}
                data-clickable={typeof props.onClick !== "undefined"}
                data-variant={variant}
                {...otherProps}
            >
                <Stack h="100%" gap={isSidebar ? 0 : (rootStackGap ?? "xl")} {...stackProps}>
                    {children}
                </Stack>
            </MantinePaper>
        </PaperContext.Provider>
    );
});

/**
 * Paper header component for displaying header content in a Paper.
 * @returns {JSX.Element} The PaperHeader component.
 */
const PaperHeader = componentWrapper<PaperHeaderFactory, IPaperHeaderProps>("PageHeader", (props, ref) => {
    const { variant } = useContext(PaperContext);
    const isSidebar = useMemo(() => variant === "sidebar", [variant]);

    return (
        <>
            <Group
                ref={ref}
                justify="space-between"
                align="center"
                {...props}
                className={clsx(styles["header"], props.className)}
            />
            {isSidebar && <Divider m={0} />}
        </>
    );
});

/**
 * Paper header title component with optional subtitle.
 * @param {string} props.subtitle - Optional subtitle text to display below the title.
 * @returns {JSX.Element} The PaperHeaderTitle component.
 */
const PaperHeaderTitle = ({ subtitle, ...otherProps }: IPaperHeaderTitleProps) => {
    return (
        <Stack gap="xs">
            <Title order={4} {...otherProps} />
            {subtitle && (
                <Text size="xs" dimmed>
                    {subtitle}
                </Text>
            )}
        </Stack>
    );
};

PaperHeader.Title = PaperHeaderTitle;

/**
 * Paper content component for displaying main content in a Paper.
 * @returns {JSX.Element} The PaperContent component.
 */
const PaperContent = componentWrapper<PaperContentFactory, IPaperContentProps>("PaperContent", (props, ref) => {
    const { variant } = useContext(PaperContext);
    return (
        <Stack
            gap="xxl"
            pl={variant === "sidebar" ? undefined : "lg"}
            ref={ref}
            {...props}
            className={clsx(styles["content"], props.className)}
        />
    );
});

const PaperContentHeader = componentWrapper<PaperContentHeaderFactory, IPaperContentHeaderProps>(
    "PaperContentHeader",
    ({ title, subtitle, ...otherProps }, ref) => {
        return (
            <Stack gap="sm" ref={ref} justify="space-between" {...otherProps}>
                <Title order={3}>{title}</Title>
                <Text variant="semibold" size="sm" dimmed>
                    {subtitle}
                </Text>
            </Stack>
        );
    }
);

/**
 * Paper footer component for displaying footer content in a Paper.
 * @returns {JSX.Element} The PaperFooter component.
 */
const PaperFooter = componentWrapper<PaperFooterFactory, IPaperFooterProps>("PaperFooter", (props, ref) => {
    const { variant } = useContext(PaperContext);
    const isSidebar = variant === "sidebar";

    return (
        <>
            {isSidebar && <Divider m={0} />}
            <Group ref={ref} {...props} className={clsx(styles["footer"], props.className)} />
        </>
    );
});

PaperContent.Header = PaperContentHeader;

Paper.Header = PaperHeader;
Paper.Content = PaperContent;
Paper.Footer = PaperFooter;

export { Paper, PaperHeader, PaperHeaderTitle, PaperContent, PaperContentHeader, PaperFooter };
